import React, { ComponentPropsWithoutRef } from 'react';
import { Flex, Modal } from 'antd';
import { StoreDetails } from '../../../../../types/store';
import useHelpers from '../../../../../hooks/utility/useHelpers';
import { EnvironmentOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import { GoogleMap, Marker } from '@react-google-maps/api';
import DeliveryZone, { DeliveryZoneIdentifier } from '../../../../store/DeliveryZone';
import { DeliveryZoneGeometryType } from '../../../../../types/constants';

type Props = ComponentPropsWithoutRef<typeof Modal> & { data: StoreDetails };

const DeliveryRadiusModal = ({ data, ...rest }: Props) => {
  const { formatCurrency } = useHelpers();
  const getKmLabelFromRadius = (radius: number) => `${~~(radius / 1000)}km`;

  return (
    <Modal
      title="Delivery radius"
      footer={null}
      {...rest}
      classNames={{
        header: '[&_*]:!text-[18px] [&_*]:!text-slate-700 !bg-slate-100',
        body: 'space-y-3',
        content: '!bg-slate-100',
      }}
    >
      <section className="border p-3 rounded-lg space-y-2 bg-slate-700/5">
        <h5 className="text-md text-slate-600">
          <EnvironmentOutlined className="mr-2 text-md" />
          {data.name}
        </h5>
        <p className="text-slate-600 text-sm">{data.location.formatted_address}</p>
      </section>
      <GoogleMap
        mapContainerClassName={classnames('min-h-[600px] h-full w-full ')}
        center={{
          lat: data.location.lat,
          lng: data.location.lng,
        }}
        zoom={11}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
        }}
      >
        <Marker
          label={data.name}
          position={{
            lat: data.location.lat,
            lng: data.location.lng,
          }}
        />
        {data.order_settings.delivery_zones.map((item) => (
          <DeliveryZone data={item} key={`delivery-zone-${item?.id}`} />
        ))}
      </GoogleMap>
      <h5 className="text-sm font-semibold text-slate-700">Delivery zones:</h5>
      {data.order_settings?.delivery_zones.map((zone) => (
        <article key={zone.id} className="space-y-2 p-3 rounded-lg border bg-white">
          <div className="flex justify-between">
            <Flex gap="small">
              <DeliveryZoneIdentifier data={zone} className="my-auto" />
              <p className="text-sm font-medium my-auto">{zone.name}</p>
            </Flex>
            <p className="rounded-full border px-2 py-1 text-xs font-medium">
              {zone.geometry_type === DeliveryZoneGeometryType.Circle &&
                getKmLabelFromRadius((zone.geometry_data as any).radius)}
            </p>
          </div>
          <p className="text-xs text-slate-600">{formatCurrency(zone.fee)}</p>
        </article>
      ))}
    </Modal>
  );
};

export default DeliveryRadiusModal;
